<div class="envelope-sign">

  <!-- Loading -->
  <div *ngIf="loading" class="envelope-sign__loading">
    <i class="fas fa-spinner fa-spin"></i>
    <p>Loading envelope...</p>
  </div>

  <!-- Not Found -->
  <div *ngIf="!loading && notFound" class="envelope-sign__not-found">
    <i class="fas fa-exclamation-triangle"></i>
    <h3>Envelope Not Found</h3>
    <p>This envelope link is invalid or has expired.</p>
  </div>

  <!-- Envelope -->
  <div *ngIf="!loading && !notFound && envelope" class="envelope-sign__card">

    <div class="envelope-sign__header">
      <div class="envelope-sign__title">
        <i class="fas fa-envelope-open-text"></i>
        <h2>Documents to Sign</h2>
      </div>

      <!-- Participant (this envelope's customer) -->
      <div class="envelope-sign__participant">
        <div class="envelope-sign__participant-name">
          <i class="fas fa-user"></i>
          <span>For <strong>{{ envelope.participant.name }}</strong></span>
          <span *ngIf="envelope.participant.role" class="envelope-sign__participant-role">
            {{ envelope.participant.role }}
          </span>
        </div>
        <div *ngIf="envelope.participant.isMinor" class="envelope-sign__minor-note">
          <i class="fas fa-info-circle"></i>
          A parent or guardian must complete these waivers on behalf of this minor.
        </div>
      </div>

      <div *ngIf="envelope.bookingLabel" class="envelope-sign__booking">
        {{ envelope.bookingLabel }}
      </div>
    </div>

    <!-- Progress -->
    <div class="envelope-sign__progress">
      <div class="envelope-sign__progress-header">
        <span>{{ signedCount }} of {{ totalCount }} complete</span>
        <span class="envelope-sign__progress-badge"
              [class.envelope-sign__progress-badge--complete]="envelope.status === 'complete'"
              [class.envelope-sign__progress-badge--partial]="envelope.status === 'partial'">
          <i class="fas"
             [class.fa-check-circle]="envelope.status === 'complete'"
             [class.fa-clock]="envelope.status !== 'complete'"></i>
          {{ envelope.status | titlecase }}
        </span>
      </div>
      <div class="envelope-sign__progress-bar">
        <div class="envelope-sign__progress-fill" [style.width.%]="progressPercent"></div>
      </div>
    </div>

    <!-- All complete banner -->
    <div *ngIf="envelope.status === 'complete'" class="envelope-sign__complete-banner">
      <i class="fas fa-check-circle"></i>
      <div>
        <strong>All waivers signed.</strong>
        <div>You're all set for your booking. You may close this page.</div>
      </div>
    </div>

    <!-- Checklist -->
    <ul class="envelope-sign__items">
      <li *ngFor="let item of envelope.items"
          class="envelope-sign__item"
          [class.envelope-sign__item--signed]="item.status === 'signed'">

        <div class="envelope-sign__item-status">
          <i class="fas"
             [class.fa-check-circle]="item.status === 'signed'"
             [class.fa-circle]="item.status === 'pending'"></i>
        </div>

        <div class="envelope-sign__item-body">
          <div class="envelope-sign__item-name">{{ item.waiverName }}</div>
          <div *ngIf="item.status === 'signed' && item.signedAt"
               class="envelope-sign__item-signed-at">
            Signed {{ item.signedAt | date:'medium' }}
            <span *ngIf="item.signedBy?.signedByGuardian" class="envelope-sign__item-guardian">
              <i class="fas fa-user-shield"></i> by guardian
            </span>
          </div>
        </div>

        <div class="envelope-sign__item-action">
          <button *ngIf="item.status === 'pending'"
                  class="btn btn-primary btn-sm"
                  (click)="openItem(item)">
            Sign <i class="fas fa-arrow-right"></i>
          </button>
          <span *ngIf="item.status === 'signed'" class="envelope-sign__item-done">
            <i class="fas fa-check"></i> Signed
          </span>
        </div>
      </li>
    </ul>

    <!-- Primary-only: manage other participants on this booking -->
    <section *ngIf="isPrimary" class="envelope-sign__participants">
      <div class="envelope-sign__participants-header">
        <h4><i class="fas fa-users"></i> Other participants</h4>
        <button type="button" class="btn btn-sm btn-outline-primary d-flex align-items-center"
                *ngIf="!showAddForm"
                (click)="openAddForm()">
          <i class="fas fa-user-plus"></i> Add
        </button>
      </div>

      <div *ngIf="lastAddedMessage" class="envelope-sign__participants-flash"
           [class.envelope-sign__participants-flash--warn]="failedEnvelopeId">
        <i class="fas"
           [class.fa-check-circle]="!failedEnvelopeId"
           [class.fa-exclamation-circle]="failedEnvelopeId"></i>
        <span class="envelope-sign__participants-flash-text">{{ lastAddedMessage }}</span>
        <button *ngIf="failedEnvelopeId" type="button"
                class="btn btn-sm btn-outline-primary envelope-sign__participants-flash-copy"
                (click)="copyEnvelopeLink()"
                [attr.title]="linkCopied ? 'Copied!' : 'Copy signing link to clipboard'">
          <i class="fas" [class.fa-copy]="!linkCopied" [class.fa-check]="linkCopied"></i>
          <span>{{ linkCopied ? 'Copied' : 'Copy Link' }}</span>
        </button>
      </div>

      <!-- Every envelope on this booking -->
      <ul class="envelope-sign__participants-list" *ngIf="bookingEnvelopes.length > 0">
        <li *ngFor="let env of bookingEnvelopes"
            class="envelope-sign__participants-item"
            [class.envelope-sign__participants-item--self]="env.id === envelope.id">
          <div class="envelope-sign__participants-main">
            <span class="envelope-sign__participants-name">
              {{ env.participant.name }}
              <span *ngIf="env.id === envelope.id" class="envelope-sign__participants-tag">you</span>
              <span *ngIf="env.participant.isMinor" class="envelope-sign__participants-tag envelope-sign__participants-tag--minor">minor</span>
            </span>
            <span class="envelope-sign__participants-contact"
                  *ngIf="env.participant.email || env.participant.phone">
              {{ env.participant.email || env.participant.phone }}
            </span>
          </div>
          <span class="envelope-sign__participants-status"
                [class.envelope-sign__participants-status--complete]="env.status === 'complete'"
                [class.envelope-sign__participants-status--partial]="env.status === 'partial'">
            {{ env.status | titlecase }}
          </span>
        </li>
      </ul>

      <!-- Add form -->
      <div *ngIf="showAddForm" class="envelope-sign__add-form">
        <div class="envelope-sign__add-field">
          <label>Name</label>
          <input type="text" class="form-control form-control-sm"
                 [(ngModel)]="addForm.name"
                 placeholder="Full name" />
        </div>

        <!-- Date of birth replaces the old "Is a minor" checkbox: the
             customer states a fact and Fleetmaid classifies it against the
             shop's configured age threshold. The guardian field below and
             the waiver variant both follow from the result. -->
        <div class="envelope-sign__add-field">
          <label for="envelopeAddDob">
            Date of birth
            <span class="envelope-sign__optional">optional</span>
          </label>
          <!-- Month/Day/Year dropdowns rather than a calendar: on a phone
               each part is a short native wheel, and the birth year is a
               type-ahead instead of paging a calendar back decades. -->
          <app-date-of-birth-picker
                 idPrefix="envelopeAddDob"
                 [small]="true"
                 [invalid]="addDobTouched && !!addDobError"
                 [(ngModel)]="addForm.dateOfBirth"
                 (ngModelChange)="onAddDobChange()"
                 (partialChange)="addDobPartial = $event"
                 (blur)="addDobTouched = true">
          </app-date-of-birth-picker>
          <div class="invalid-feedback d-block" *ngIf="addDobTouched && !!addDobError">
            {{ addDobError }}
          </div>
          <!-- Age echo: confirms the year was read as intended. -->
          <small class="text-muted" *ngIf="!addDobError && addDobAge !== null">
            Age {{ addDobAge }}<ng-container *ngIf="addForm.isMinor">
              — under {{ minorAgeThreshold }}, a parent or guardian signs on their behalf</ng-container>
          </small>
        </div>

        <div class="envelope-sign__add-field" *ngIf="addForm.isMinor">
          <label>Parent / Guardian</label>
          <input type="text" class="form-control form-control-sm"
                 list="envelopeGuardianSuggestions"
                 autocomplete="off"
                 [(ngModel)]="addForm.guardianName"
                 placeholder="Type a name or pick from the list" />
          <datalist id="envelopeGuardianSuggestions">
            <option *ngFor="let name of guardianSuggestions()" [value]="name"></option>
          </datalist>
        </div>

        <div class="envelope-sign__add-field">
          <label>
            <ng-container *ngIf="!addForm.isMinor">Email</ng-container>
            <ng-container *ngIf="addForm.isMinor">Parent/Guardian Email</ng-container>
          </label>
          <input #addEmailInput="ngModel" type="email" class="form-control form-control-sm"
                 name="envelopeSignAddEmail"
                 [class.is-invalid]="addEmailInput.touched && addEmailError"
                 [(ngModel)]="addForm.email"
                 placeholder="name@example.com" />
          <div class="invalid-feedback" *ngIf="addEmailInput.touched && addEmailError">
            {{ addEmailError }}
          </div>
        </div>

        <div class="envelope-sign__add-field">
          <label>
            <ng-container *ngIf="!addForm.isMinor">Phone</ng-container>
            <ng-container *ngIf="addForm.isMinor">Parent/Guardian Phone</ng-container>
          </label>
          <input #addPhoneInput="ngModel" phoneFormat type="tel" class="form-control form-control-sm"
                 name="envelopeSignAddPhone"
                 [class.is-invalid]="addPhoneInput.touched && addPhoneError"
                 [(ngModel)]="addForm.phone"
                 placeholder="(555) 555-5555" />
          <div class="invalid-feedback" *ngIf="addPhoneInput.touched && addPhoneError">
            {{ addPhoneError }}
          </div>
        </div>

        <div *ngIf="addError" class="envelope-sign__add-error">
          <i class="fas fa-exclamation-circle"></i> {{ addError }}
        </div>

        <div class="envelope-sign__add-actions">
          <button type="button" class="btn btn-sm btn-secondary"
                  (click)="cancelAdd()" [disabled]="adding">
            Cancel
          </button>
          <button type="button" class="btn btn-sm btn-primary"
                  (click)="saveAdd()" [disabled]="!canSaveAdd || adding">
            <i class="fas" [ngClass]="adding ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
            {{ adding ? 'Sending...' : 'Add &amp; send envelope' }}
          </button>
        </div>
      </div>
    </section>

    <div class="envelope-sign__footer">
      <small>&copy; {{ currentYear }} Fleet Adventures. All rights reserved.</small>
    </div>
  </div>
</div>
